import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  X, Upload, FileSpreadsheet, ChevronRight, ArrowLeft,
} from 'lucide-react';
import { MODULE_SCHEMAS, parseCsv, autoMapHeaders, validateRows, isSupportedFile } from '../data/masterData';
import { parseXlsx } from '../data/xlsxParser';
import { cn } from '@/lib/utils';

import UploadStepper from './upload/UploadStepper';
import UploadStep from './upload/UploadStep';
import PreviewStep from './upload/PreviewStep';
import MappingStep from './upload/MappingStep';
import ValidateStep from './upload/ValidateStep';
import SchemaStep from './upload/SchemaStep';
import PublishStep from './upload/PublishStep';
import { downloadCsvTemplate } from './upload/csvTemplate';

// Re-export so existing consumers (`MasterConfigPage.jsx`) continue to import from this file.
export { downloadCsvTemplate };

export default function UploadMasterDialog({ open, moduleKey, onClose, onSave }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [parsed, setParsed] = useState(null); // { headers, rows, sheetNames }
  const [columnMap, setColumnMap] = useState({});
  const [dragOver, setDragOver] = useState(false);

  const schema = moduleKey ? MODULE_SCHEMAS[moduleKey] : null;

  useEffect(() => {
    if (open) {
      setStep(0);
      setFile(null);
      setParsed(null);
      setColumnMap({});
      setParseError(null);
    }
  }, [open, moduleKey]);

  const handleFile = async (f) => {
    if (!f) return;
    const sup = isSupportedFile(f);
    if (!sup.ok) {
      setParseError(sup.reason);
      return;
    }
    setParseError(null);
    setFile(f);
    setParsing(true);
    try {
      const isCsv = f.name.toLowerCase().endsWith('.csv') || f.type === 'text/csv';
      if (isCsv) {
        const text = await f.text();
        const result = parseCsv(text);
        const rowSheets = result.rows.map(() => 'Sheet1');
        setParsed({ ...result, sheetNames: ['Sheet1'], rowSheets });
        setColumnMap(autoMapHeaders(moduleKey, result.headers));
      } else {
        // Real .xlsx / .xls parse via SheetJS
        const result = await parseXlsx(f);
        if (!result.headers.length) {
          throw new Error('Workbook contains no readable header row.');
        }
        if (!result.rows.length) {
          throw new Error('Workbook contains no data rows after the header.');
        }
        setParsed(result);
        setColumnMap(autoMapHeaders(moduleKey, result.headers));
      }
      setStep(1); // → Preview
    } catch (e) {
      setParseError(`Failed to parse file: ${e.message}`);
    } finally {
      setParsing(false);
    }
  };

  const validation = useMemo(() => {
    if (!parsed || !schema) return null;
    return validateRows(moduleKey, parsed.headers, parsed.rows, columnMap);
  }, [parsed, columnMap, moduleKey, schema]);

  const errorCount = validation?.issues.filter((i) => i.severity === 'error').length || 0;
  const warningCount = validation?.issues.filter((i) => i.severity === 'warning').length || 0;
  const mappedCount = useMemo(
    () => schema ? schema.fields.filter((f) => columnMap[f.key] != null && columnMap[f.key] >= 0).length : 0,
    [columnMap, schema]
  );
  const mandatoryUnmapped = useMemo(
    () => schema ? schema.fields.filter((f) => f.mandatory && (columnMap[f.key] == null || columnMap[f.key] < 0)).length : 0,
    [columnMap, schema]
  );

  const handleSave = (action) => {
    if (!validation) return;
    // Stamp provenance on every row so the Source badge can render downstream.
    const rowSheets = parsed?.rowSheets || [];
    const fileName = file?.name || '';
    const objectsWithSource = validation.objects.map((obj, i) => ({
      ...obj,
      _sourceFile: fileName,
      _sourceSheet: rowSheets[i] || (parsed?.sheetNames?.[0] || 'Sheet1'),
    }));
    onSave(moduleKey, objectsWithSource, {
      fileName,
      headers: parsed?.headers,
      sheetNames: parsed?.sheetNames,
      columnMap,
      action,
    });
    onClose();
  };

  if (!open || !schema) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[94vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: schema.color + '15', color: schema.color }}>
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Upload {schema.label}</h2>
              <p className="text-xs text-slate-500">CSV or Excel (.xlsx, .xls) · max 5 MB · auto-detects columns</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <UploadStepper step={step} />

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 0 && (
            <UploadStep
              schema={schema}
              moduleKey={moduleKey}
              fileRef={fileRef}
              parsing={parsing}
              parseError={parseError}
              dragOver={dragOver}
              setDragOver={setDragOver}
              onFile={handleFile}
            />
          )}
          {step === 1 && parsed && <PreviewStep file={file} parsed={parsed} />}
          {step === 2 && parsed && (
            <MappingStep
              schema={schema}
              parsed={parsed}
              columnMap={columnMap}
              setColumnMap={setColumnMap}
              mappedCount={mappedCount}
              mandatoryUnmapped={mandatoryUnmapped}
            />
          )}
          {step === 3 && validation && (
            <ValidateStep validation={validation} errorCount={errorCount} warningCount={warningCount} />
          )}
          {step === 4 && validation && <SchemaStep schema={schema} validation={validation} />}
          {step === 5 && validation && <PublishStep validation={validation} onSave={handleSave} />}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <div className="text-[11px] text-slate-500 truncate min-w-0 flex-1 mr-4">
            {file ? <>File: <span className="font-mono text-slate-700">{file.name}</span></> : 'No file selected'}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
            )}
            <button onClick={onClose} className="px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 rounded-lg transition">
              Cancel
            </button>
            {step === 0 && (
              <button
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-semibold rounded-lg shadow-sm"
              >
                <Upload className="w-3.5 h-3.5" />
                Browse files
              </button>
            )}
            {step === 1 && (
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg"
              >
                Continue to Mapping
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
            {step === 2 && (
              <button
                onClick={() => setStep(3)}
                disabled={mandatoryUnmapped > 0}
                className={cn(
                  'inline-flex items-center gap-1.5 px-4 py-2 text-white text-xs font-semibold rounded-lg',
                  mandatoryUnmapped > 0
                    ? 'bg-slate-300 cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800'
                )}
              >
                Continue to Validate
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
            {step === 3 && (
              <button
                onClick={() => setStep(4)}
                disabled={errorCount > 0}
                className={cn(
                  'inline-flex items-center gap-1.5 px-4 py-2 text-white text-xs font-semibold rounded-lg',
                  errorCount > 0
                    ? 'bg-slate-300 cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800'
                )}
              >
                Generate Schema
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
            {step === 4 && (
              <button
                onClick={() => setStep(5)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg"
                data-testid="continue-to-publish"
              >
                Continue to Publish
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
